import { useEffect, useRef, useState } from "react";

/**
 * V7.1 / V15.x: Pull-to-refresh em mobile (Android/iOS/web).
 * Detecta arrasto a partir do topo da página (scroll da window) e dispara `onRefresh()`.
 *
 * V15.x fixes:
 *  - iOS: usa refs (sem re-registrar listeners a cada frame) + touchcancel; considera scrollTop<=0
 *    (o WKWebView pode reportar scroll levemente negativo no rubber-band).
 *  - Indicador respeita a safe-area (ver PullIndicator).
 *
 * Uso:
 *   const ptr = usePullToRefresh(load);
 *   <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
 */
export default function usePullToRefresh(onRefresh, opts = {}) {
  const { threshold = 80, enabled = true } = opts;
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef(null);
  const activeRef = useRef(false);
  const distRef = useRef(0);
  const refreshingRef = useRef(false);
  const fnRef = useRef(onRefresh);

  useEffect(() => { fnRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const scrollTop = () =>
      window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const onStart = (e) => {
      if (refreshingRef.current) return;
      // Só arma o gesto se estivermos no topo (>2px = já rolou, ignora)
      if (scrollTop() > 2) { activeRef.current = false; return; }
      const t = e.touches?.[0];
      if (!t) return;
      startYRef.current = t.clientY;
      activeRef.current = true;
      distRef.current = 0;
    };

    const onMove = (e) => {
      if (!activeRef.current || startYRef.current == null) return;
      if (scrollTop() > 2) { activeRef.current = false; distRef.current = 0; setPullDistance(0); return; }
      const t = e.touches?.[0];
      if (!t) return;
      const dy = t.clientY - startYRef.current;
      if (dy <= 0) { distRef.current = 0; setPullDistance(0); return; }
      // Resistência: fica mais difícil quanto mais estica
      const dist = Math.min(dy * 0.5, threshold * 1.5);
      distRef.current = dist;
      setPullDistance(dist);
    };

    const onEnd = async () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      const dist = distRef.current;
      startYRef.current = null;
      if (dist >= threshold && !refreshingRef.current) {
        refreshingRef.current = true;
        setIsRefreshing(true);
        setPullDistance(threshold);
        try { await fnRef.current?.(); } catch (e) { console.error("[pullToRefresh]", e); }
        setTimeout(() => {
          refreshingRef.current = false;
          setIsRefreshing(false);
          setPullDistance(0);
          distRef.current = 0;
        }, 350);
      } else {
        setPullDistance(0);
        distRef.current = 0;
      }
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, threshold]);

  const progress = Math.min(pullDistance / threshold, 1);
  return { isPulling: pullDistance > 0, progress, pullDistance, isRefreshing };
}
